import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, Users, BarChart3, Instagram, Facebook, Loader2, History, RefreshCw, Eye } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { concorrentesAPI, instancesAPI } from '../services/api';
import { toast } from 'react-toastify';

const CompetitiveIntelligence = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('concorrentes');
  const [concorrentes, setConcorrentes] = useState([]);
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [analyzingAll, setAnalyzingAll] = useState(false);

  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [selectedConcorrente, setSelectedConcorrente] = useState(null);
  const [selectedAdversario, setSelectedAdversario] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [showHistorico, setShowHistorico] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    loadConcorrentes();
  }, []);

  const loadConcorrentes = async () => {
    try {
      setLoading(true);
      const response = await concorrentesAPI.list();
      console.log('Concorrentes carregados:', response.data);
      setConcorrentes(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Erro ao carregar:', error);
      toast.error('Erro ao carregar concorrentes');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (data) => {
    try {
      const res = await concorrentesAPI.create(data);
      toast.success('Concorrente adicionado! Analisando...');
      setShowAddModal(false);
      loadConcorrentes();
      handleAnalisar(res.data);
    } catch (error) {
      toast.error('Erro ao adicionar concorrente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este concorrente?')) return;
    try {
      await concorrentesAPI.delete(id);
      toast.success('Concorrente removido!');
      loadConcorrentes();
    } catch (error) {
      toast.error('Erro ao remover concorrente');
    }
  };

  const handleAnalisar = async (concorrente) => {
    try {
      setAnalyzing(concorrente.id);
      const instancesResponse = await instancesAPI.list(user.cliente_id);
      const metaInstance = instancesResponse.data.find(i => 
        (i.platform === 'instagram' || i.platform === 'facebook') && i.access_token
      );
      
      if (!metaInstance) {
        toast.error('Você precisa conectar o Instagram para analisar concorrentes');
        return;
      }

      const res = await concorrentesAPI.analisar(concorrente.id, metaInstance.access_token, days);
      toast.success(`Análise atualizada! ${res.data.all_posts?.length || 0} posts coletados dos últimos ${days} dias`);
      const updatedList = await loadConcorrentes();
      // Atualizar selectedAnalysis se for o concorrente selecionado
      if (selectedAnalysis && selectedConcorrente?.id === concorrente.id) {
        const updated = (updatedList || concorrentes).find(c => c.id === concorrente.id);
        if (updated?.metricas_snapshot) {
          setSelectedAnalysis(updated.metricas_snapshot);
          setSelectedConcorrente(updated);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao analisar concorrente');
    } finally {
      setAnalyzing(null);
    }
  };

  const handleAnalisarTodos = async () => {
    try {
      setAnalyzingAll(true);
      const instancesResponse = await instancesAPI.list(user.cliente_id);
      const metaInstance = instancesResponse.data.find(i => 
        (i.platform === 'instagram' || i.platform === 'facebook') && i.access_token
      );
      if (!metaInstance) { toast.error('Conecte o Instagram primeiro'); return; }
      const results = await Promise.allSettled(
        concorrentes.map(c => concorrentesAPI.analisar(c.id, metaInstance.access_token, days))
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      toast.success(`${ok}/${concorrentes.length} concorrentes atualizados (${days} dias)!`);
      loadConcorrentes();
    } catch (error) {
      toast.error('Erro ao atualizar concorrentes');
    } finally {
      setAnalyzingAll(false);
    }
  };

  const tabs = [
    { id: 'concorrentes', label: 'Concorrentes', icon: Users },
    { id: 'trends', label: 'Trends', icon: TrendingUp },
    { id: 'comparativo', label: 'Comparativo', icon: BarChart3 },
    { id: 'monitoramento', label: 'Monitoramento', icon: Eye },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title="Inteligência Competitiva"
          subtitle="Monitore concorrentes, descubra trends e gere ideias de conteúdo"
        />

          {/* Tabs */}
          <div className={`flex gap-2 mb-6 border-b overflow-x-auto ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 sm:px-4 py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm sm:text-base ${
                    activeTab === tab.id
                      ? darkMode
                        ? 'border-blue-500 text-blue-400'
                        : 'border-blue-600 text-blue-600'
                      : darkMode
                        ? 'border-transparent text-gray-400 hover:text-gray-300'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Filtro de período global */}
          <div className="flex gap-2 flex-wrap mb-4">
            {[7, 15, 30, 60, 90].map(d => (
              <button key={d} onClick={() => { setDays(d); setTrends(null); }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${days === d
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >{d} dias</button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 'concorrentes' && (
            <div>
              {/* Header com botão adicionar */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Concorrentes Monitorados
                  </h2>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Adicione contas para monitorar e analisar
                  </p>
                </div>
                <div className="flex gap-2">
                  <select
                    value={tipoFilter}
                    onChange={(e) => setTipoFilter(e.target.value)}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="todos">Todos</option>
                    <option value="benchmark">📊 Benchmark</option>
                    <option value="adversario">⚔️ Adversário</option>
                    <option value="observacao">👁️ Observação</option>
                  </select>
                  <button
                    onClick={handleAnalisarTodos}
                    disabled={analyzingAll || concorrentes.length === 0}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${analyzingAll ? 'animate-spin' : ''}`} />
                    {analyzingAll ? 'Atualizando...' : 'Atualizar Todos'}
                  </button>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Concorrente
                  </button>
                </div>
              </div>

              {/* Lista de concorrentes */}
              {loading ? (
                <div className="text-center py-12">
                  <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Carregando...</p>
                </div>
              ) : concorrentes.length === 0 ? (
                <div className={`text-center py-12 rounded-lg border-2 border-dashed ${
                  darkMode ? 'border-zinc-700 bg-zinc-800' : 'border-gray-300 bg-white'
                }`}>
                  <Users className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Nenhum concorrente monitorado
                  </h3>
                  <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Comece adicionando contas de concorrentes para monitorar
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Adicionar Primeiro Concorrente
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {concorrentes.filter(c => tipoFilter === 'todos' || c.tipo === tipoFilter).map(conc => (
                    <div
                      key={conc.id}
                      className={`p-4 rounded-lg border ${
                        darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${
                            conc.platform === 'instagram'
                              ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                              : 'bg-blue-600'
                          }`}>
                            {conc.profile_pic_url ? (
                              <img src={conc.profile_pic_url} alt="" className="w-full h-full object-cover" />
                            ) : conc.platform === 'instagram' ? (
                              <Instagram className="w-6 h-6 text-white" />
                            ) : (
                              <Facebook className="w-6 h-6 text-white" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {conc.nome}
                              </h3>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                conc.tipo === 'benchmark' ? 'bg-green-500/20 text-green-400' :
                                conc.tipo === 'adversario' ? 'bg-red-500/20 text-red-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {conc.tipo === 'benchmark' ? '📊' : conc.tipo === 'adversario' ? '⚔️' : '👁️'}
                              </span>
                            </div>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              @{conc.username}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(conc.id)}
                          className={`text-sm ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-center">
                        {[
                          { label: 'Seguidores', value: conc.metricas_snapshot?.concorrente?.followers, fmt: v => v?.toLocaleString('pt-BR') },
                          { label: 'Engajamento', value: conc.metricas_snapshot?.concorrente?.avg_engagement_rate, fmt: v => `${v}%` },
                          { label: 'Posts analisados', value: conc.metricas_snapshot?.all_posts?.length || conc.metricas_snapshot?.concorrente?.total_posts, fmt: v => v },
                          { label: 'Frequência', value: conc.metricas_snapshot?.concorrente?.frequency, fmt: v => ({ daily: 'Diário', every_2_days: 'A cada 2 dias', weekly: 'Semanal', multiple_daily: 'Várias/dia', irregular: 'Irregular' }[v] || v) },
                        ].map((item, i) => (
                          <div key={i}>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.label}</p>
                            <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.value ? item.fmt(item.value) : '-'}
                            </p>
                          </div>
                        ))}
                      </div>
                      {conc.metricas_snapshot?.concorrente?.best_hour && (
                        <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          ⏰ Posta mais às {conc.metricas_snapshot.concorrente.best_hour} ({conc.metricas_snapshot.concorrente.best_day || ''})
                        </p>
                      )}
                      {conc.metricas_snapshot?.comparativo && (
                        <p className={`text-xs mt-1 ${conc.metricas_snapshot?.comparativo?.engajamento?.includes('✅') ? 'text-green-500' : 'text-yellow-500'}`}>
                          {conc.metricas_snapshot?.comparativo?.engajamento}
                        </p>
                      )}
                      {conc.last_analysis_at && (
                        <p className={`text-[11px] mt-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                          Atualizado {new Date(conc.last_analysis_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleAnalisar(conc)}
                          disabled={analyzing === conc.id}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                            darkMode
                              ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600 disabled:opacity-50'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                          }`}
                          title="Atualizar dados"
                        >
                          {analyzing === conc.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : '🔄'}
                        </button>
                        {conc.metricas_snapshot?.dicas && (
                          <button
                            onClick={() => { setSelectedAnalysis(conc.metricas_snapshot); setSelectedConcorrente(conc); setActiveTab('comparativo'); }}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                              darkMode ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            📊 Comparar
                          </button>
                        )}
                        {conc.last_analysis_at && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await concorrentesAPI.historico(conc.id);
                                setHistorico(res.data);
                                setShowHistorico(true);
                              } catch { toast.error('Erro ao carregar histórico'); }
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            title="Histórico de análises"
                          >
                            <History className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'trends' && (
            <TrendsTab darkMode={darkMode} trends={trends} loading={loadingTrends} days={days} onLoad={async (d) => {
              setLoadingTrends(true);
              try {
                const res = await concorrentesAPI.trends(d || days);
                setTrends(res.data);
              } catch (e) { toast.error('Analise pelo menos um concorrente primeiro'); }
              finally { setLoadingTrends(false); }
            }} />
          )}



          {activeTab === 'comparativo' && (
            selectedAnalysis ? (() => {
              const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
              const filteredTopPosts = (selectedAnalysis.all_posts || selectedAnalysis.top_posts_concorrente || [])
                .filter(p => new Date(p.timestamp) >= cutoff)
                .sort((a, b) => ((b.likes || 0) + (b.comments || 0)) - ((a.likes || 0) + (a.comments || 0)))
                .slice(0, 10);
              return (
              <div className="space-y-6">
                {/* Dicas Acionáveis */}
                {selectedAnalysis.dicas?.length > 0 && (
                  <div className={`p-5 rounded-lg border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      💡 O que fazer
                    </h3>
                    <div className="space-y-2">
                      {selectedAnalysis.dicas.map((dica, i) => (
                        <div key={i} className={`flex gap-3 p-3 rounded-lg ${darkMode ? 'bg-zinc-700/50' : 'bg-blue-50'}`}>
                          <span className="text-lg">→</span>
                          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{dica}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Análise de Adversário */}
                {selectedConcorrente?.tipo === 'adversario' && selectedAnalysis.analise_adversario && (
                  <div className={`p-5 rounded-lg border-2 ${darkMode ? 'bg-red-950/20 border-red-900' : 'bg-red-50 border-red-200'}`}>
                    <h3 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
                      ⚔️ Análise de Adversário
                    </h3>
                    <div className="space-y-3">
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-800/50' : 'bg-white'}`}>
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>🎯 Pontos fracos detectados:</p>
                        <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedAnalysis.analise_adversario.pontos_fracos.map((p, i) => (
                            <li key={i}>• {p}</li>
                          ))}
                        </ul>
                      </div>
                      <div className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-800/50' : 'bg-white'}`}>
                        <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>💡 Como se diferenciar:</p>
                        <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {selectedAnalysis.analise_adversario.como_diferenciar.map((p, i) => (
                            <li key={i}>• {p}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Comparativo lado a lado */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { data: selectedAnalysis.voce, label: 'Você', color: 'green' },
                    { data: selectedAnalysis.concorrente, label: 'Concorrente', color: 'blue' },
                  ].map(({ data, label, color }) => data && (
                    <div key={label} className={`p-5 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-3 mb-4">
                        {data.profile_picture_url ? (
                          <img src={data.profile_picture_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${label === 'Você' ? 'bg-green-500' : 'bg-blue-500'} text-white font-bold`}>
                            {(data.username || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>@{data.username}</h3>
                          <span className={`text-xs ${label === 'Você' ? 'text-green-500' : 'text-blue-500'}`}>{label}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { label: 'Seguidores', value: data.followers?.toLocaleString('pt-BR') },
                          { label: 'Engajamento', value: `${data.avg_engagement_rate}%` },
                          { label: 'Frequência', value: ({ daily: 'Diário', every_2_days: 'A cada 2 dias', weekly: 'Semanal', multiple_daily: 'Várias vezes ao dia', irregular: 'Irregular' }[data.frequency] || data.frequency) },
                          { label: 'Melhor horário', value: `${data.best_day || ''} ${data.best_hour || ''}` },
                        ].map(item => (
                          <div key={item.label}>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</p>
                            <p className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.value || '-'}</p>
                          </div>
                        ))}
                      </div>
                      {data.media_types && (
                        <div className="mt-4">
                          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Tipos de conteúdo</p>
                          <div className="flex gap-2">
                            {Object.entries(data.media_types).map(([type, count]) => (
                              <span key={type} className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                {type.toLowerCase() === 'video' ? '🎬 Reels' : type.toLowerCase() === 'image' ? '📷 Fotos' : '📑 Carrossel'}: {count}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {data.top_hashtags?.length > 0 && (
                        <div className="mt-4">
                          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Top hashtags</p>
                          <div className="flex flex-wrap gap-1">
                            {data.top_hashtags.slice(0, 6).map(h => (
                              <span key={h} className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{h}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Insights extras calculados dos posts filtrados */}
                {filteredTopPosts.length > 0 && (() => {
                  const posts = filteredTopPosts;
                  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

                  // Melhor formato por engajamento
                  const byType = {};
                  posts.forEach(p => {
                    const t = (p.media_type || 'IMAGE').toUpperCase();
                    if (!byType[t]) byType[t] = { total: 0, count: 0 };
                    byType[t].total += (p.likes || 0) + (p.comments || 0);
                    byType[t].count += 1;
                  });
                  const formatRank = Object.entries(byType)
                    .map(([t, v]) => ({ tipo: t === 'VIDEO' ? '🎬 Reels' : t === 'CAROUSEL_ALBUM' ? '📑 Carrossel' : '📷 Foto', avg: Math.round(v.total / v.count), count: v.count }))
                    .sort((a, b) => b.avg - a.avg);

                  // Horários de pico
                  const byHour = {};
                  posts.forEach(p => {
                    const h = new Date(p.timestamp).getHours();
                    if (!byHour[h]) byHour[h] = { total: 0, count: 0 };
                    byHour[h].total += (p.likes || 0) + (p.comments || 0);
                    byHour[h].count += 1;
                  });
                  const hourRank = Object.entries(byHour)
                    .map(([h, v]) => ({ hora: `${h}:00`, avg: Math.round(v.total / v.count), posts: v.count }))
                    .sort((a, b) => b.avg - a.avg)
                    .slice(0, 5);

                  // Palavras-chave (excluindo stopwords e hashtags)
                  const stopwords = new Set(['a','o','e','de','da','do','em','que','um','uma','para','com','não','por','se','na','no','os','as','dos','das','ao','é','mais','como','mas','foi','eu','ele','ela','isso','esse','essa','são','tem','já','está','ser','ter','seu','sua','nos','me','muito','também','só','ou','quando','até','bem','vai','vou','tá','pra','pro','meu','minha','aqui','lá','todo','toda','todos','todas','esse','essa','esses','essas','este','esta','estes','estas','isso','isto','nosso','nossa','nossos','nossas','dele','dela','deles','delas','num','numa','pelo','pela','pelos','pelas','entre','sobre','sem','depois','antes','ainda','mesmo','então','porque','onde','quem','qual','cada','outro','outra','outros','outras','nós','eles','elas','vocês','você','gente','dia','ano','vez','coisa','mundo','vida','tempo','forma','parte','lugar','caso','ponto','fato','modo','tipo','jeito','hora','vezes','anos','dias','pessoas','país','governo','trabalho','casa','homem','mulher','olhos','mão','cabeça','corpo','rua','cidade','noite','água','terra','mar','sol','céu','rei','deus','senhor','conta','lado','fim','meio','momento','história','nome','palavra','coração','porta','caminho','mesa','mãos','olho','pé','voz','cara','filho','pai','mãe','irmão','amigo','mulheres','homens','filhos','crianças','repost']);
                  const wordCounts = {};
                  posts.forEach(p => {
                    (p.caption || '').toLowerCase().replace(/#[\w\u00C0-\u024F]+/g, '').replace(/[^\w\sà-ú]/g, '').split(/\s+/).forEach(w => {
                      if (w.length > 3 && !stopwords.has(w)) wordCounts[w] = (wordCounts[w] || 0) + 1;
                    });
                  });
                  const topWords = Object.entries(wordCounts).sort(([,a],[,b]) => b - a).slice(0, 15);

                  const card = (title, children) => (
                    <div className={`p-5 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                      {children}
                    </div>
                  );

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {card('🎬 Melhor formato (engajamento)', (
                        <div className="space-y-3">
                          {formatRank.map((f, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className={`text-sm ${i === 0 ? 'font-bold' : ''} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {i === 0 ? '🥇 ' : ''}{f.tipo}
                              </span>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ~{f.avg.toLocaleString('pt-BR')} eng · {f.count} posts
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}

                      {card('⏰ Horários de pico', (
                        <div className="space-y-2">
                          {hourRank.map((h, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className={`text-sm ${i === 0 ? 'font-bold' : ''} ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {i === 0 ? '🥇 ' : i === 1 ? '🥈 ' : i === 2 ? '🥉 ' : '   '}{h.hora}
                              </span>
                              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ~{h.avg.toLocaleString('pt-BR')} eng · {h.posts} posts
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}

                      {card('💬 Palavras-chave das legendas', (
                        <div className="flex flex-wrap gap-1.5">
                          {topWords.map(([word, count], i) => (
                            <span key={i} className={`text-xs px-2 py-1 rounded-full ${
                              i < 3 ? 'bg-blue-500/20 text-blue-400 font-medium' :
                              i < 8 ? (darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700') :
                              (darkMode ? 'bg-zinc-700/50 text-gray-500' : 'bg-gray-50 text-gray-400')
                            }`}>
                              {word} ({count})
                            </span>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Evolução temporal + Taxa de resposta + Qualidade */}
                {filteredTopPosts.length > 2 && (() => {
                  const posts = filteredTopPosts;

                  // 2. Evolução temporal — engajamento por semana
                  const weekMap = {};
                  posts.forEach(p => {
                    const d = new Date(p.timestamp);
                    const weekStart = new Date(d);
                    weekStart.setDate(d.getDate() - d.getDay());
                    const key = weekStart.toISOString().slice(0, 10);
                    if (!weekMap[key]) weekMap[key] = { total: 0, count: 0 };
                    weekMap[key].total += (p.likes || 0) + (p.comments || 0);
                    weekMap[key].count += 1;
                  });
                  const weekData = Object.entries(weekMap)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([week, v]) => ({
                      semana: new Date(week).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
                      engajamento: Math.round(v.total / v.count),
                      posts: v.count,
                    }));

                  // 7. Taxa de resposta (comments/likes ratio)
                  const totalLikes = posts.reduce((s, p) => s + (p.likes || 0), 0);
                  const totalComments = posts.reduce((s, p) => s + (p.comments || 0), 0);
                  const commentRatio = totalLikes > 0 ? ((totalComments / totalLikes) * 100).toFixed(1) : 0;

                  // 6. Post mais engajado vs mais recente
                  const sorted = [...posts].sort((a, b) => ((b.likes || 0) + (b.comments || 0)) - ((a.likes || 0) + (a.comments || 0)));
                  const bestPost = sorted[0];
                  const recentPost = [...posts].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
                  const bestEng = bestPost ? (bestPost.likes || 0) + (bestPost.comments || 0) : 0;
                  const recentEng = recentPost ? (recentPost.likes || 0) + (recentPost.comments || 0) : 0;

                  // 1. Calendário de atividade (últimos dias)
                  const activityMap = {};
                  posts.forEach(p => {
                    const key = new Date(p.timestamp).toISOString().slice(0, 10);
                    activityMap[key] = (activityMap[key] || 0) + 1;
                  });
                  const today = new Date();
                  const calDays = [];
                  for (let i = days - 1; i >= 0; i--) {
                    const d = new Date(today);
                    d.setDate(d.getDate() - i);
                    const key = d.toISOString().slice(0, 10);
                    calDays.push({ date: key, count: activityMap[key] || 0, day: d.getDay() });
                  }

                  const card2 = (title, children) => (
                    <div className={`p-5 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
                      {children}
                    </div>
                  );

                  return (
                    <div className="space-y-4">
                      {/* Evolução temporal */}
                      {weekData.length > 1 && card2('📈 Evolução do engajamento por semana', (
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={weekData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#eee'} />
                            <XAxis dataKey="semana" tick={{ fontSize: 11, fill: darkMode ? '#888' : '#666' }} />
                            <YAxis tick={{ fontSize: 11, fill: darkMode ? '#888' : '#666' }} />
                            <Tooltip contentStyle={{ background: darkMode ? '#1f1f1f' : '#fff', border: darkMode ? '1px solid #333' : '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }} />
                            <Line type="monotone" dataKey="engajamento" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="Eng. médio" />
                          </LineChart>
                        </ResponsiveContainer>
                      ))}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Taxa de resposta */}
                        {card2('💬 Taxa de conversa', (
                          <div className="space-y-3">
                            <div className="flex items-end gap-2">
                              <span className={`text-3xl font-bold ${parseFloat(commentRatio) > 3 ? 'text-green-500' : parseFloat(commentRatio) > 1 ? 'text-yellow-500' : 'text-red-400'}`}>
                                {commentRatio}%
                              </span>
                              <span className={`text-xs mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>comments/likes</span>
                            </div>
                            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {parseFloat(commentRatio) > 3 ? 'Excelente! Conteúdo gera muita conversa' : parseFloat(commentRatio) > 1 ? 'Bom nível de interação' : 'Conteúdo gera mais curtidas passivas'}
                            </p>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              ❤️ {totalLikes.toLocaleString('pt-BR')} likes · 💬 {totalComments.toLocaleString('pt-BR')} comments
                            </div>
                          </div>
                        ))}

                        {/* Qualidade subindo ou caindo */}
                        {bestPost && recentPost && card2('📊 Tendência de qualidade', (
                          <div className="space-y-3">
                            <div>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>🏆 Melhor post</p>
                              <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{bestEng.toLocaleString('pt-BR')} interações</p>
                              <p className={`text-[11px] line-clamp-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{bestPost.caption?.substring(0, 80)}</p>
                            </div>
                            <div>
                              <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>🕐 Post mais recente</p>
                              <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{recentEng.toLocaleString('pt-BR')} interações</p>
                              <p className={`text-[11px] line-clamp-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{recentPost.caption?.substring(0, 80)}</p>
                            </div>
                            <p className={`text-xs font-medium ${recentEng >= bestEng * 0.7 ? 'text-green-500' : 'text-red-400'}`}>
                              {recentEng >= bestEng * 0.7 ? '📈 Qualidade se mantendo' : '📉 Engajamento recente abaixo do melhor'}
                            </p>
                          </div>
                        ))}

                        {/* Calendário de atividade */}
                        {card2(`📅 Atividade (${days} dias)`, (
                          <div>
                            <ResponsiveContainer width="100%" height={150}>
                              <BarChart data={(() => {
                                const weekMap = {};
                                calDays.forEach(d => {
                                  const dt = new Date(d.date);
                                  const ws = new Date(dt); ws.setDate(dt.getDate() - dt.getDay());
                                  const key = ws.toISOString().slice(0, 10);
                                  weekMap[key] = (weekMap[key] || 0) + d.count;
                                });
                                return Object.entries(weekMap).sort(([a],[b]) => a.localeCompare(b)).map(([w, c]) => ({
                                  semana: new Date(w).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
                                  posts: c,
                                }));
                              })()}>
                                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#e5e7eb'} />
                                <XAxis dataKey="semana" tick={{ fontSize: 10, fill: darkMode ? '#888' : '#666' }} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: darkMode ? '#888' : '#666' }} />
                                <Tooltip contentStyle={{ background: darkMode ? '#1f1f1f' : '#fff', border: darkMode ? '1px solid #333' : '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="posts" fill="#22c55e" radius={[4, 4, 0, 0]} name="Posts" />
                              </BarChart>
                            </ResponsiveContainer>
                            <p className={`text-center text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {calDays.filter(d => d.count > 0).length} dias com posts · {calDays.reduce((s, d) => s + d.count, 0)} posts total
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Top Posts filtrados por período */}
                {filteredTopPosts.length > 0 && (
                  <div className={`p-5 rounded-lg border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      🏆 Posts com mais engajamento ({days} dias)
                    </h3>
                    <div className="space-y-4">
                      {filteredTopPosts.map((post, i) => {
                        const analise = (selectedAnalysis.analise_estrategica || []).find(a => a.post_id === post.id);
                        return (
                          <div key={i} className={`rounded-xl border overflow-hidden ${darkMode ? 'border-zinc-700 bg-zinc-700/30' : 'border-gray-200 bg-white'}`}>
                            <div className="flex gap-4 p-4">
                              <a href={post.permalink} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-zinc-800">
                                  {post.media_url ? (
                                    <img src={post.media_url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-2xl">📷</div>
                                  )}
                                  <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm ${
                                    (post.media_type || '').toUpperCase() === 'VIDEO' ? 'bg-purple-500/80 text-white' :
                                    (post.media_type || '').toUpperCase() === 'CAROUSEL_ALBUM' ? 'bg-blue-500/80 text-white' :
                                    'bg-black/50 text-white'
                                  }`}>
                                    {(post.media_type || '').toUpperCase() === 'VIDEO' ? '▶' : (post.media_type || '').toUpperCase() === 'CAROUSEL_ALBUM' ? '◫' : '📷'}
                                  </span>
                                </div>
                              </a>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>❤️ {(post.likes || 0).toLocaleString('pt-BR')}</span>
                                  <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>💬 {(post.comments || 0).toLocaleString('pt-BR')}</span>
                                </div>
                                <p className={`text-xs line-clamp-2 mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {post.caption || 'Sem legenda'}
                                </p>
                                {analise && (
                                  <div className={`mt-3 p-4 rounded-lg border-l-4 ${darkMode ? 'bg-zinc-800 border-blue-500' : 'bg-blue-50 border-blue-400'}`}>
                                    <p className={`text-xs font-semibold mb-2 flex items-center gap-2 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                      <span>🤖</span>
                                      <span>Análise Estratégica</span>
                                    </p>
                                    <div className={`text-xs leading-relaxed space-y-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      {analise.analise_estrategica.split(/\d+\.\s\*\*/).filter(Boolean).map((section, idx) => {
                                        const [title, ...content] = section.split('**');
                                        return (
                                          <div key={idx}>
                                            <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                              {idx + 1}. {title?.trim()}
                                            </p>
                                            <p className="mt-1">{content.join('').trim()}</p>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              );
            })() : (
              <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                <BarChart3 className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Análise Comparativa
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Clique em "Analisar" em um concorrente para ver a comparação
                </p>
              </div>
            )
          )}

          {/* Aba Monitoramento */}
          {activeTab === 'monitoramento' && (() => {
            const adversarios = concorrentes.filter(c => c.tipo === 'adversario');

            if (adversarios.length === 0) {
              return (
                <div className="space-y-6">
                  <div>
                    <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>👁️ Monitoramento de Adversários</h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Posts recentes dos seus adversários diretos</p>
                  </div>
                  <div className={`text-center py-12 rounded-xl ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
                    <Eye className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhum adversário cadastrado. Adicione concorrentes do tipo "Adversário" para monitorar.</p>
                  </div>
                </div>
              );
            }

            const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
            
            // Filtrar adversários se selecionado um específico
            const adversariosFiltrados = selectedAdversario 
              ? adversarios.filter(a => a.id === selectedAdversario)
              : adversarios;
            
            // Consolidar dados
            const allPosts = [];
            const statsPerAdv = adversariosFiltrados.map(adv => {
              const posts = (adv.metricas_snapshot?.all_posts || [])
                .filter(p => new Date(p.timestamp) >= cutoff)
                .map(p => ({ ...p, adversario: adv.nome, username: adv.username }));
              allPosts.push(...posts);
              
              const totalEng = posts.reduce((s, p) => s + (p.likes || 0) + (p.comments || 0), 0);
              return {
                adversario: adv,
                posts: posts.length,
                engajamento: posts.length ? Math.round(totalEng / posts.length) : 0,
              };
            });

            allPosts.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

            // Hashtags consolidadas
            const hashtags = {};
            allPosts.forEach(p => {
              const matches = p.caption?.match(/#[\w\u00C0-\u024F]+/g) || [];
              matches.forEach(h => hashtags[h] = (hashtags[h] || 0) + 1);
            });
            const topHashtags = Object.entries(hashtags).sort(([,a], [,b]) => b - a).slice(0, 12);

            // Palavras-chave consolidadas
            const stopwords = ['a','o','e','de','da','do','em','que','um','uma','para','com','não','por','se','na','no','os','as','dos','das','ao','é','mais','como','mas','foi','eu','ele','ela','isso','esse','essa','são','tem','já','está','ser','ter','seu','sua','nos','me','muito','também','só','ou','quando','até','bem','vai','vou','tá','pra','pro','meu','minha','aqui','lá','todo','toda','todos','todas'];
            const words = {};
            allPosts.forEach(p => {
              const text = p.caption?.toLowerCase().replace(/#\S+/g, '').replace(/[^\wÀ-ÿ\s]/g, ' ') || '';
              text.split(/\s+/).forEach(w => {
                if (w.length > 3 && !stopwords.includes(w)) words[w] = (words[w] || 0) + 1;
              });
            });
            const topWords = Object.entries(words).sort(([,a], [,b]) => b - a).slice(0, 15);

            // Evolução semanal consolidada
            const weekMap = {};
            allPosts.forEach(p => {
              const dt = new Date(p.timestamp);
              const ws = new Date(dt); ws.setDate(dt.getDate() - dt.getDay());
              const key = ws.toISOString().slice(0, 10);
              if (!weekMap[key]) weekMap[key] = { posts: 0, eng: 0 };
              weekMap[key].posts++;
              weekMap[key].eng += (p.likes || 0) + (p.comments || 0);
            });
            const weekData = Object.entries(weekMap).sort(([a],[b]) => a.localeCompare(b)).map(([w, d]) => ({
              semana: new Date(w).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
              posts: d.posts,
            }));

            return (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>👁️ Monitoramento de Adversários</h2>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedAdversario ? adversariosFiltrados[0]?.nome : `${adversarios.length} adversário${adversarios.length > 1 ? 's' : ''}`} · {allPosts.length} posts nos últimos {days} dias
                    </p>
                  </div>
                  <select value={selectedAdversario || ''} onChange={(e) => setSelectedAdversario(e.target.value || null)}
                    className={`px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                    <option value="">Todos os adversários</option>
                    {adversarios.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
                  </select>
                </div>

                {/* Cards de resumo */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {statsPerAdv.map(s => (
                    <div key={s.adversario.id} className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {s.adversario.profile_pic_url && <img src={s.adversario.profile_pic_url} alt="" className="w-8 h-8 rounded-full" />}
                        <p className={`text-xs font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{s.adversario.nome}</p>
                      </div>
                      <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{s.posts}</p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>posts · {s.engajamento} eng/post</p>
                    </div>
                  ))}
                </div>

                {/* Gráficos e análises */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Evolução */}
                  {weekData.length > 1 && (
                    <div className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>📈 Atividade semanal</h4>
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={weekData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#333' : '#e5e7eb'} />
                          <XAxis dataKey="semana" tick={{ fontSize: 10, fill: darkMode ? '#888' : '#666' }} />
                          <YAxis tick={{ fontSize: 10, fill: darkMode ? '#888' : '#666' }} />
                          <Tooltip contentStyle={{ background: darkMode ? '#1f1f1f' : '#fff', border: darkMode ? '1px solid #333' : '1px solid #e5e7eb', borderRadius: 8, fontSize: 11 }} />
                          <Bar dataKey="posts" fill="#ef4444" radius={[4, 4, 0, 0]} name="Posts" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Hashtags */}
                  {topHashtags.length > 0 && (
                    <div className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                      <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>#️⃣ Hashtags mais usadas</h4>
                      <div className="flex flex-wrap gap-2">
                        {topHashtags.map(([h, c]) => (
                          <span key={h} className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                            {h} <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>({c})</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Temas */}
                {topWords.length > 0 && (
                  <div className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                    <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>💬 Temas mais abordados</h4>
                    <div className="flex flex-wrap gap-2">
                      {topWords.map(([w, c]) => (
                        <span key={w} className={`text-sm px-3 py-1.5 rounded-full font-medium ${darkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'}`}>
                          {w} <span className="opacity-60 text-xs">({c})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grid de posts */}
                <div className={`p-4 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
                  <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>📸 Posts recentes</h4>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                    {allPosts.slice(0, 18).map((post, i) => (
                      <a key={i} href={post.permalink} target="_blank" rel="noopener noreferrer"
                        className={`group relative rounded-lg overflow-hidden border ${darkMode ? 'border-zinc-700 hover:border-red-500' : 'border-gray-200 hover:border-red-400'} transition-colors`}>
                        {post.media_url && (
                          <img src={post.media_url} alt="" className="w-full aspect-square object-cover" />
                        )}
                        <div className={`absolute inset-0 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2`}>
                          <p className="text-white text-[9px] font-medium mb-1">@{post.username}</p>
                          <p className="text-white text-[10px] line-clamp-2 mb-1">{post.caption}</p>
                          <div className="flex gap-2 text-white text-[10px]">
                            <span>❤️ {post.likes}</span>
                            <span>💬 {post.comments}</span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

      {/* Modal Histórico */}
      {showHistorico && historico && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowHistorico(false)}>
          <div className={`max-w-lg w-full rounded-lg max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-zinc-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  📈 Histórico — @{historico.concorrente?.username}
                </h2>
                <button onClick={() => setShowHistorico(false)} className={darkMode ? 'text-gray-400' : 'text-gray-600'}>✕</button>
              </div>
              {historico.analises?.length === 0 ? (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Nenhuma análise registrada ainda.</p>
              ) : (
                <div className="space-y-3">
                  {historico.analises.map((a, i) => {
                    const prev = historico.analises[i + 1];
                    const followersDiff = prev ? a.followers - prev.followers : null;
                    const engDiff = prev ? (a.engagement_rate - prev.engagement_rate).toFixed(2) : null;
                    return (
                      <div key={a.id} className={`p-3 rounded-lg border ${darkMode ? 'bg-zinc-700/50 border-zinc-600' : 'bg-gray-50 border-gray-200'}`}>
                        <p className={`text-xs mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {new Date(a.data).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Seguidores</p>
                            <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{a.followers?.toLocaleString('pt-BR') || '-'}</p>
                            {followersDiff !== null && followersDiff !== 0 && (
                              <p className={`text-xs ${followersDiff > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {followersDiff > 0 ? '+' : ''}{followersDiff.toLocaleString('pt-BR')}
                              </p>
                            )}
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Engajamento</p>
                            <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{a.engagement_rate ? `${a.engagement_rate}%` : '-'}</p>
                            {engDiff !== null && engDiff !== '0.00' && (
                              <p className={`text-xs ${parseFloat(engDiff) > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {parseFloat(engDiff) > 0 ? '+' : ''}{engDiff}%
                              </p>
                            )}
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Frequência</p>
                            <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {{ daily: 'Diário', every_2_days: '2 dias', weekly: 'Semanal', multiple_daily: 'Várias/dia', irregular: 'Irregular' }[a.frequency] || a.frequency || '-'}
                            </p>
                          </div>
                          <div>
                            <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Posts analisados</p>
                            <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{a.total_posts || '-'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Concorrente */}
      {showAddModal && (
        <AddCompetitorModal
          darkMode={darkMode}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAdd}
        />
      )}
    </Layout>
  );
};

const AddCompetitorModal = ({ darkMode, onClose, onAdd }) => {
  const [form, setForm] = useState({
    nome: '',
    username: '',
    platform: 'instagram',
    tipo: 'benchmark',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
        <div className="p-6">
          <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Adicionar Concorrente
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome
              </label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-zinc-700 border-zinc-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Username (sem @)
              </label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value.replace('@', '') })}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-zinc-700 border-zinc-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Plataforma
              </label>
              <select
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-zinc-700 border-zinc-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="instagram">Instagram</option>
                <option value="facebook">Facebook</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Tipo
              </label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border ${
                  darkMode
                    ? 'bg-zinc-700 border-zinc-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="benchmark">📊 Benchmark (Inspiração)</option>
                <option value="adversario">⚔️ Adversário (Oposição)</option>
                <option value="observacao">👁️ Observação (Mercado)</option>
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 px-4 py-2 rounded-lg font-medium ${
                  darkMode
                    ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Adicionar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const TrendsTab = ({ darkMode, trends, loading, onLoad, days }) => {
  useEffect(() => { if (!trends && !loading) onLoad(days); }, []);
  useEffect(() => { onLoad(days); }, [days]);

  if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" /><p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Analisando trends do nicho...</p></div>;
  if (!trends || trends.message) return (
    <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
      <TrendingUp className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{trends?.message || 'Analise pelo menos um concorrente primeiro'}</p>
    </div>
  );

  const card = (title, children) => (
    <div className={`p-5 rounded-xl border ${darkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}`}>
      <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Resumo do período */}
      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        📊 {trends.total_posts} posts analisados nos últimos {trends.periodo}
      </div>

      {/* Dicas */}
      {trends.dicas?.length > 0 && (
        <div className={`p-5 rounded-xl border-2 ${darkMode ? 'bg-zinc-800 border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-blue-400' : 'text-blue-700'}`}>💡 Insights do nicho</h3>
          <div className="space-y-2">
            {trends.dicas.map((d, i) => (
              <p key={i} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>→ {d}</p>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Melhor tipo de conteúdo */}
        {trends.tipos_conteudo?.length > 0 && card('🎬 Formato mais usado', (
          <div className="space-y-3">
            {trends.tipos_conteudo.map((t, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-lg">{t.tipo === 'Reels' ? '🎬' : t.tipo === 'Carrossel' ? '📑' : '📷'}</span>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t.tipo}</span>
                    <span className={`text-sm font-bold ${i === 0 ? 'text-green-500' : (darkMode ? 'text-gray-300' : 'text-gray-700')}`}>
                      {t.percentual}%
                    </span>
                  </div>
                  <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>
                    <div className={`h-2 rounded-full transition-all ${i === 0 ? 'bg-green-500' : i === 1 ? 'bg-yellow-500' : 'bg-gray-400'}`}
                      style={{ width: `${t.percentual}%` }} />
                  </div>
                  <span className={`text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t.quantidade} posts · ~{t.avg_engagement} interações/post</span>
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Melhores horários */}
        {trends.melhores_horarios?.length > 0 && card('⏰ Melhores horários (por engajamento)', (
          <div className="space-y-2">
            {trends.melhores_horarios.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className={`text-sm font-medium ${i === 0 ? (darkMode ? 'text-green-400' : 'text-green-600') : (darkMode ? 'text-white' : 'text-gray-900')}`}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '  '} {h.horario}
                </span>
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>~{h.avg_engagement} eng · {h.posts} posts</span>
              </div>
            ))}
            {trends.melhores_dias?.length > 0 && (
              <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
                <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Melhores dias</p>
                <div className="flex gap-2">
                  {trends.melhores_dias.slice(0, 3).map((d, i) => (
                    <span key={i} className={`text-xs px-2 py-1 rounded-full ${i === 0 ? 'bg-green-500/20 text-green-400' : (darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
                      {d.dia} · ~{d.avg_engagement} eng
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Hashtags do nicho */}
        {trends.hashtags?.length > 0 && card('# Hashtags do nicho', (
          <div className="flex flex-wrap gap-1.5">
            {trends.hashtags.map((h, i) => (
              <span key={i} className={`text-xs px-2 py-1 rounded-full ${
                i < 3 ? 'bg-green-500/20 text-green-400 font-medium' :
                i < 7 ? (darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-700') :
                (darkMode ? 'bg-zinc-700/50 text-gray-500' : 'bg-gray-50 text-gray-400')
              }`}>
                {h.tag}
              </span>
            ))}
          </div>
        ))}

        {/* Frequência do nicho */}
        {trends.frequencia_por_conta?.length > 0 && card('📅 Frequência de postagem', (
          <div className="space-y-2">
            {trends.frequencia_por_conta.map((f, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>@{f.username}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  f.posts_por_semana >= 5 ? 'bg-green-500/20 text-green-400' :
                  f.posts_por_semana >= 2 ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>{f.posts_no_periodo} posts · {f.posts_por_semana}/sem</span>
              </div>
            ))}
          </div>
        ))}

        {/* Top posts do período */}
        {trends.top_posts?.length > 0 && card(`🔥 Top posts (${trends.periodo})`, (
          <div className="space-y-3">
            {trends.top_posts.slice(0, 5).map((p, i) => (
              <div key={i} className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="flex justify-between items-start">
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>@{p.username}</span>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>❤️{p.likes} 💬{p.comments}</span>
                </div>
                <p className={`text-xs mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.caption}</p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompetitiveIntelligence;
